import { IterableEmbeddedManager } from '@iterable/web-sdk';

import type { IterableEmbeddedMessage } from '@iterable/web-sdk';

/**
 * Iterable Embedded Messaging service.
 *
 * Wraps `IterableEmbeddedManager` from `@iterable/web-sdk` and exposes a
 * React-friendly subscription model: components call `subscribe()` and re-render
 * whenever Iterable returns new content for a placement.
 *
 * The SDK does NOT automatically track impressions for embedded messages —
 * impression accounting is handled separately by `useEmbeddedImpression` via
 * `notifyImpression` / `notifyImpressionEnded` below. Those currently fan out
 * to a no-op session handler; EN-11787 will plug `IterableEmbeddedSessionManager`
 * into `setSessionHandler` so impressions land in `embeddedSession` payloads.
 */

const APP_PACKAGE_NAME = 'metropolis-customer-web';

/**
 * LocalStorage key holding a JSON array of dismissed Iterable messageIds.
 * Iterable's server keeps returning the message in `syncMessages` until the
 * campaign ends, so we suppress dismissed messages client-side across reloads.
 */
const DISMISSED_STORAGE_KEY = 'iterable.embedded.dismissed';

function loadDismissedIds(): Set<string> {
  try {
    const raw = window.localStorage.getItem(DISMISSED_STORAGE_KEY);
    if (!raw) return new Set();
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? new Set(parsed.filter((v) => typeof v === 'string')) : new Set();
  } catch {
    return new Set();
  }
}

function persistDismissedIds(ids: Set<string>): void {
  try {
    window.localStorage.setItem(DISMISSED_STORAGE_KEY, JSON.stringify([...ids]));
  } catch {
    // Storage unavailable (private mode, quota). Dismissal still works in-memory for this session.
  }
}

export interface EmbeddedSessionHandler {
  pauseImpression(messageId: string): void;
  startImpression(messageId: string, placementId: number): void;
}

type Listener = () => void;

class EmbeddedService {
  private manager: IterableEmbeddedManager | null = null;
  private listeners = new Set<Listener>();
  private sessionHandler: EmbeddedSessionHandler | null = null;
  /**
   * Cache of `getMessagesForPlacement` results keyed by placementId. The SDK
   * builds a fresh array on each call (filter()), so we memoize here to keep
   * snapshot identity stable for `useSyncExternalStore`. Invalidated by
   * `notify()`.
   */
  private placementCache = new Map<number, IterableEmbeddedMessage[]>();
  private dismissedIds: Set<string> = loadDismissedIds();

  private getManager(): IterableEmbeddedManager {
    if (!this.manager) {
      this.manager = new IterableEmbeddedManager(APP_PACKAGE_NAME);
      this.manager.addUpdateListener({
        onMessagesUpdated: () => this.notify(),
        onEmbeddedMessagingDisabled: () => this.notify(),
      });
    }
    return this.manager;
  }

  /**
   * Fetch current content for the given placements from Iterable. Cadence is
   * caller-controlled — see callers in `Customer.tsx` and `useEmbeddedSync`.
   */
  async syncMessages(placementIds: number[]): Promise<void> {
    const manager = this.getManager();
    await manager.syncMessages(APP_PACKAGE_NAME, () => this.notify(), placementIds);
  }

  getMessagesForPlacement(placementId: number): IterableEmbeddedMessage[] {
    if (!this.manager) return EMPTY_MESSAGES;
    const cached = this.placementCache.get(placementId);
    if (cached) return cached;
    const fresh = this.manager
      .getMessagesForPlacement(placementId)
      .filter((m) => !this.dismissedIds.has(m.metadata.messageId));
    this.placementCache.set(placementId, fresh);
    return fresh;
  }

  /**
   * Mark a message as dismissed. Persists the id to localStorage so the
   * message stays hidden across reloads, and notifies subscribers so it is
   * removed from the next render.
   *
   * Note: `@iterable/web-sdk` does not currently re-export `trackEmbeddedDismiss`
   * from its public barrel, so server-side dismiss tracking is not wired up.
   * Local filtering still gives the user-facing behavior they expect.
   */
  dismiss(messageId: string): void {
    if (this.dismissedIds.has(messageId)) return;
    this.dismissedIds.add(messageId);
    persistDismissedIds(this.dismissedIds);
    this.notify();
  }

  /** React-friendly subscription. Returns an unsubscribe function. */
  subscribe(listener: Listener): () => void {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Register the embedded-session handler. EN-11787 wires
   * `IterableEmbeddedSessionManager` in here; until then handler is null and
   * impression notifications are no-ops.
   */
  setSessionHandler(handler: EmbeddedSessionHandler | null): void {
    this.sessionHandler = handler;
  }

  notifyImpression(messageId: string, placementId: number): void {
    this.sessionHandler?.startImpression(messageId, placementId);
  }

  notifyImpressionEnded(messageId: string): void {
    this.sessionHandler?.pauseImpression(messageId);
  }

  /** Forward a click through the SDK so it logs `embeddedClick`. */
  click(clickedUrl: string | null): void {
    this.manager?.click(clickedUrl);
  }

  private notify(): void {
    this.placementCache.clear();
    this.listeners.forEach((listener) => {
      listener();
    });
  }
}

const EMPTY_MESSAGES: IterableEmbeddedMessage[] = [];

const IterableEmbeddedService = new EmbeddedService();

export default IterableEmbeddedService;
